/**
 * Moteur de l'analyseur d'accroche LinkedIn : pur, sans React.
 *
 * Aucune de ces règles n'est publiée par LinkedIn, et aucune ne prédit une
 * portée. Ce sont des repères éditoriaux NeverZero appliqués à ce que le lecteur
 * voit AVANT « voir plus » (coupure estimée par `postSimulator.computeCut`).
 * L'outil classe l'accroche par type, repère ses défauts dans le texte lui-même,
 * et propose une réécriture à partir des mots de l'auteur quand c'est possible.
 */
import type { Severity } from '@/lib/postSimulator';
import { DEVICES, computeCut, wrapLines } from '@/lib/postSimulator';

export type HookType = 'chiffre' | 'liste' | 'question' | 'histoire' | 'contre-pied' | 'annonce' | 'affirmation';

export interface HookTypeInfo {
  label: string;
  /** Ce dont ce type d'accroche a besoin pour tenir sa promesse. */
  needs: string;
}

export const HOOK_TYPES: Record<HookType, HookTypeInfo> = {
  chiffre: {
    label: 'Accroche chiffrée',
    needs: "Le chiffre doit être un résultat, avec un point de comparaison (avant/après, une durée). Un chiffre seul, sans contexte, n'intrigue pas.",
  },
  liste: {
    label: 'Accroche en liste',
    needs: "« N choses » promet un contenu à parcourir : le nombre doit être petit et le sujet précis, sinon le lecteur n'y voit qu'un modèle de plus.",
  },
  question: {
    label: 'Accroche en question',
    needs: "Une question ouverte (comment, pourquoi, combien) ouvre une boucle. Une question fermée appelle un « oui » ou un « non » mental, puis le lecteur passe.",
  },
  histoire: {
    label: 'Accroche en récit',
    needs: "Un récit tient s'il pose très vite une situation concrète et un enjeu. La date ou le lieu ne suffisent pas : le conflit doit arriver dans la première ligne.",
  },
  'contre-pied': {
    label: 'Accroche à contre-pied',
    needs: "Prendre le contre-pied d'une idée reçue fonctionne si le lecteur reconnaît cette idée. Nommez-la précisément, sinon l'opposition est vide.",
  },
  annonce: {
    label: 'Accroche de politesse',
    needs: "Cette ouverture annonce qu'on va parler au lieu de parler. L'espace avant « voir plus » est limité : la première phrase utile doit passer devant.",
  },
  affirmation: {
    label: 'Accroche en affirmation',
    needs: "Une affirmation nue tient si elle est spécifique et un peu risquée. Si elle est vraie pour tout le monde, elle n'apprend rien au lecteur.",
  },
};

export interface HookCheck {
  id: 'opener' | 'length' | 'specific' | 'tension' | 'question' | 'noise';
  severity: Severity;
  title: string;
  detail: string;
  fix?: string;
}

export interface HookMark {
  start: number;
  end: number;
  kind: 'bad' | 'good';
}

export interface HookResult {
  /** Texte normalisé (retours à la ligne unifiés, apostrophes conservées). */
  text: string;
  /** Position de la coupure mobile estimée dans `text` ; `text.length` si le texte tient en entier. */
  cutIdx: number;
  truncated: boolean;
  type: HookType;
  firstLine: string;
  checks: HookCheck[];
  marks: HookMark[];
  passed: number;
  total: number;
  /** Début de phrase prêt à l'emploi tiré des mots de l'auteur, si on peut en proposer un. */
  rewrite: string | null;
}

/** Ouvertures qui annoncent qu'on va parler sans rien dire. L'apostrophe typographique est normalisée avant test. */
const GENERIC_OPENERS: RegExp[] = [
  /^(bonjour|salut|hello|coucou|bonsoir)\b(?: à (?:tous|toutes|toutes et à tous|tous et toutes)| tout le monde| mon réseau| le réseau| linkedin| à vous)?[ ,!.:]*/i,
  /^aujourd'hui,? (?:je|nous|on) (?:voulais|voulions|veux|voulons|vais|allons|tenais|tenions|souhaitais|souhaitions|souhaite|souhaitons) (?:vous )?(?:partager|parler|annoncer|revenir sur|présenter)(?: avec vous)?(?: de| d'| que| qu')?\s*/i,
  /^(?:je|nous) (?:suis|sommes) (?:très |tellement |super |trop |si )?(?:ravi|ravie|heureux|heureuse|fier|fière|content|contente|excité|excitée|fiers|ravis|heureux|contents)(?:\s+et\s+(?:fier|fière|ravi|ravie|heureux|heureuse|fiers|ravis))? (?:de|d')\s*(?:vous )?(?:annoncer|partager|présenter|vous annoncer)?(?: que| qu')?\s*/i,
  /^petit (?:post|message|retour|partage|mot)\b[ ,:.!]*/i,
  /^(?:je me permets|permettez-moi) (?:de |d')?\s*/i,
  /^(?:dans le monde d'aujourd'hui|dans un monde (?:où|qui)|à l'heure (?:où|de)|à l'ère (?:de|du|des))[ ,]*/i,
];

const CLOSED_QUESTION = /^(?:est-ce (?:que|qu')|avez-vous|saviez-vous|êtes-vous|pensez-vous|connaissez-vous|voulez-vous|vous (?:êtes|avez|savez|pensez|voulez)\b[^?\n]*\?|peut-on|doit-on|faut-il|y a-t-il)/i;
const OPEN_QUESTION = /^(?:comment|pourquoi|combien|quel(?:le)?s?|que|qu'|quand|où|qui|lequel|laquelle)\b/i;

const TENSION_WORDS =
  /\b(?:mais|pourtant|sans|jamais|erreur|faux|fausse|arrêt\w*|stop|perdu|perdre|échec|échou\w*|contraire|personne ne|au lieu de|plutôt que|malgré|seulement|le problème|la vérité|mythe|ennemi|piège|raté|regret\w*|avant|aucun\w*)\b/i;

const NUMBER = /\d+(?:[.,]\d+)?(?:\s?(?:%|€|k\b|M\b|h\b|jours?|mois|ans?|semaines?|heures?|minutes?|clients?|posts?|abonnés?))?/giu;
const EMOJI = /\p{Extended_Pictographic}/u;
const EMOJI_G = /\p{Extended_Pictographic}/gu;
const LINK = /(https?:\/\/[^\s]+|www\.[^\s]+|\b[a-z0-9-]+\.(?:com|fr|io|co|net|org|ai|app|dev|me|ly|eu)(?:\/[^\s]*)?\b)/giu;
const HASHTAG = /(^|\s)(#[\p{L}\p{N}_]+)/gu;

export function normalize(text: string): string {
  return text.replace(/\r\n?/g, '\n').trim();
}

/** Mots capitalisés qui ne sont pas en début de phrase : noms propres probables (outil, marque, personne). */
function properNouns(text: string): HookMark[] {
  const out: HookMark[] = [];
  const re = /[\p{L}][\p{L}'’-]*/gu;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    const word = m[0];
    if (!/^\p{Lu}\p{Ll}{2,}/u.test(word)) continue;
    const before = text.slice(0, m.index).replace(/\s+$/, '');
    // Début de texte, de ligne ou de phrase : une majuscule normale, pas un nom propre.
    if (before === '' || /[.!?…:\n]$/.test(before) || /\n\s*$/.test(text.slice(0, m.index))) continue;
    out.push({ start: m.index, end: m.index + word.length, kind: 'good' });
  }
  return out;
}

function classify(firstLine: string, opener: boolean): HookType {
  const l = firstLine.toLowerCase().replace(/’/g, "'").replace(/^[^\p{L}\p{N}]+/u, '').trim();
  if (opener) return 'annonce';
  if (/^\d+\s+(?:façons|manières|erreurs|raisons|choses|conseils|leçons|règles|idées|outils|questions|signes|étapes)/.test(l)) return 'liste';
  if (l.includes('?') && (OPEN_QUESTION.test(l) || CLOSED_QUESTION.test(l))) return 'question';
  if (/^(?:il y a|hier|ce matin|l'an dernier|l'année dernière|en \d{4}|j'ai|on m'a|mon (?:client|manager|patron|ancien)|un (?:client|recruteur|inconnu))/.test(l)) return 'histoire';
  if (/^(?:arrêtez|stop|non,|personne|le problème|la vérité|mythe|ne |n')/.test(l) || /\bpas\b[^.!?\n]*\bmais\b/.test(l) || /\bfaux\b/.test(l)) return 'contre-pied';
  if (/\d/.test(l.slice(0, 40))) return 'chiffre';
  return 'affirmation';
}

function firstSentence(line: string): string {
  const m = line.match(/^.*?[.!?…](?=\s|$)/);
  return (m ? m[0] : line).trim();
}

function capitalize(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

export function analyzeHook(input: string): HookResult {
  const text = normalize(input);
  const cut = computeCut(text, 'mobile');
  const truncated = cut.truncated;
  const cutIdx = truncated ? Math.max(0, text.length - cut.hidden.length) : text.length;
  const lines = text.split('\n');
  const firstLine = lines[0] ?? '';
  const matchable = text.replace(/’/g, "'");
  const marks: HookMark[] = [];
  const checks: HookCheck[] = [];

  // 1. Ouverture de politesse.
  let openerEnd = -1;
  for (const re of GENERIC_OPENERS) {
    const m = matchable.match(re);
    if (m && m[0].trim().length > 0) {
      openerEnd = m[0].length;
      break;
    }
  }
  const isOpener = openerEnd > 0;
  let rewrite: string | null = null;
  if (isOpener) {
    marks.push({ start: 0, end: openerEnd, kind: 'bad' });
    const rest = text.slice(openerEnd).replace(/^[\s,:;.!–-]+/, '');
    const restSentence = firstSentence(rest.split('\n')[0] ?? '');
    if (restSentence.length >= 12) {
      rewrite = capitalize(restSentence.length > 90 ? `${restSentence.slice(0, restSentence.lastIndexOf(' ', 90))}…` : restSentence);
    }
    checks.push({
      id: 'opener',
      severity: 'problem',
      title: `Ouverture de politesse : « ${text.slice(0, openerEnd).trim()} »`,
      detail: `Ces ${openerEnd} caractères occupent la place la plus chère du post, avant « voir plus », et ne disent rien du sujet.`,
      fix: rewrite
        ? `Commencez directement par ce qui suit : « ${rewrite} »`
        : 'Supprimez cette phrase et commencez par le fait, le chiffre ou la situation qui fait l\'intérêt du post.',
    });
  } else {
    checks.push({
      id: 'opener',
      severity: 'ok',
      title: 'Pas d\'ouverture de politesse',
      detail: 'Le premier mot du post porte déjà du sujet.',
    });
  }

  // 2. Longueur de la première ligne face à la coupure mobile.
  const { width, lines: maxLines } = DEVICES.mobile;
  const firstWrapped = wrapLines(firstLine, width).length;
  const sentence = firstSentence(firstLine);
  const hasNumber = /\d/.test(firstLine);
  if (firstWrapped >= maxLines) {
    checks.push({
      id: 'length',
      severity: 'problem',
      title: `Première ligne trop longue : ${firstLine.length} caractères, ${firstWrapped} lignes sur mobile`,
      detail: `Sur mobile, environ ${maxLines} lignes sont visibles : cette première ligne occupe tout l'espace et la coupure tombe dedans, avant que l'idée soit complète.`,
      fix: 'Gardez dans la première ligne une seule idée en moins de 80 caractères, puis un retour à la ligne.',
    });
  } else if (firstWrapped === maxLines - 1 && firstLine.length > 2 * width - 4) {
    checks.push({
      id: 'length',
      severity: 'warning',
      title: `Première ligne longue : ${firstLine.length} caractères`,
      detail: 'Elle tient avant la coupure, mais sur deux lignes pleines elle laisse peu de place à une seconde ligne qui relance.',
      fix: 'Visez 40 à 80 caractères : le lecteur lit la phrase d\'un seul regard.',
    });
  } else if (firstLine.replace(/\s+/g, ' ').length < 15 && !hasNumber) {
    checks.push({
      id: 'length',
      severity: 'warning',
      title: `Première ligne très courte : ${firstLine.length} caractères`,
      detail: 'Une ligne aussi brève peut accrocher, à condition que la suivante révèle vite le sujet. Seule, elle ne dit pas de quoi parle le post.',
      fix: 'Vérifiez que la deuxième ligne, visible elle aussi, donne le sujet précis.',
    });
  } else {
    checks.push({
      id: 'length',
      severity: 'ok',
      title: `Première ligne lisible d'un regard (${sentence.length} caractères)`,
      detail: 'Elle tient entière avant la coupure estimée sur mobile.',
    });
  }

  // 3. Précision : chiffre, durée, nom propre.
  const visibleText = text.slice(0, cutIdx);
  const numberMarks: HookMark[] = [];
  for (const m of visibleText.matchAll(NUMBER)) {
    numberMarks.push({ start: m.index ?? 0, end: (m.index ?? 0) + m[0].length, kind: 'good' });
  }
  const nounMarks = properNouns(text.slice(0, cutIdx || text.length)).filter((n) => n.start < cutIdx);
  const fullNumber = text.match(NUMBER);
  if (numberMarks.length > 0 || nounMarks.length > 0) {
    marks.push(...numberMarks, ...nounMarks);
    const pieces = [
      numberMarks.length ? `${numberMarks.length} élément${numberMarks.length > 1 ? 's' : ''} chiffré${numberMarks.length > 1 ? 's' : ''}` : '',
      nounMarks.length ? `${nounMarks.length} nom${nounMarks.length > 1 ? 's' : ''} propre${nounMarks.length > 1 ? 's' : ''} probable${nounMarks.length > 1 ? 's' : ''}` : '',
    ].filter(Boolean);
    checks.push({
      id: 'specific',
      severity: 'ok',
      title: `Précis avant la coupure : ${pieces.join(' et ')}`,
      detail: 'Un chiffre ou un nom propre dans la partie visible rend la promesse vérifiable, donc plus crédible qu\'une formule générale.',
    });
  } else if (truncated && fullNumber) {
    checks.push({
      id: 'specific',
      severity: 'warning',
      title: 'Le chiffre arrive après « voir plus »',
      detail: `Votre texte contient « ${fullNumber[0].trim()} », mais il est masqué sur mobile : le lecteur ne voit que du général.`,
      fix: 'Remontez ce chiffre dans la première ligne, c\'est lui qui donne envie de déplier.',
    });
  } else {
    checks.push({
      id: 'specific',
      severity: 'warning',
      title: 'Aucun chiffre ni nom propre dans la partie visible',
      detail: 'Une accroche sans élément précis (durée, montant, outil, personne) peut s\'appliquer à n\'importe quel post, donc n\'importe quel auteur.',
      fix: 'Ajoutez la donnée concrète du post : « en 6 semaines », « 3 clients », le nom de l\'outil ou de la situation.',
    });
  }

  // 4. Tension : une opposition, un manque ou une boucle ouverte.
  const firstTwo = lines.slice(0, 2).join(' ');
  const tensionMatch = firstTwo.match(TENSION_WORDS);
  const openLoop = /[:…]\s*$/.test(firstLine) || /\?\s*$/.test(firstLine);
  if (tensionMatch || openLoop) {
    if (tensionMatch && tensionMatch.index !== undefined) {
      // Position dans le texte d'origine, la ligne 1 + 2 étant jointe par un espace de même longueur.
      marks.push({ start: tensionMatch.index, end: tensionMatch.index + tensionMatch[0].length, kind: 'good' });
    }
    checks.push({
      id: 'tension',
      severity: 'ok',
      title: tensionMatch ? `Une tension est posée (« ${tensionMatch[0].toLowerCase()} »)` : 'Une boucle est ouverte',
      detail: tensionMatch
        ? 'Un contraste ou un manque donne une raison de lire la suite : le lecteur veut savoir comment ça se résout.'
        : 'La première ligne se termine sur une question ou une suite attendue : la réponse est derrière « voir plus ».',
    });
  } else {
    checks.push({
      id: 'tension',
      severity: 'warning',
      title: 'Rien ne s\'oppose, rien ne manque',
      detail: 'La première ligne énonce un fait qui se suffit à lui-même. Sans contraste ni question en suspens, le lecteur n\'a pas de raison de déplier.',
      fix: 'Opposez deux choses (« avant / après », « on pensait / on a vu ») ou terminez la ligne en laissant la réponse à la suite.',
    });
  }

  // 5. Question fermée.
  const cleanFirst = firstLine.replace(/’/g, "'").replace(/^[^\p{L}\p{N}]+/u, '').trim();
  if (cleanFirst.includes('?') && CLOSED_QUESTION.test(cleanFirst)) {
    checks.push({
      id: 'question',
      severity: 'warning',
      title: 'Question fermée dans la première ligne',
      detail: 'Une question à laquelle on répond par oui ou non est tranchée en une seconde : le lecteur répond dans sa tête et passe.',
      fix: 'Reformulez avec « comment », « pourquoi » ou « combien », ou affirmez la réponse puis ouvrez le « pourquoi ».',
    });
  } else if (cleanFirst.includes('?')) {
    checks.push({
      id: 'question',
      severity: 'ok',
      title: 'Question ouverte',
      detail: 'Elle ne se règle pas par oui ou non, la réponse est dans la suite du post.',
    });
  } else {
    checks.push({
      id: 'question',
      severity: 'ok',
      title: 'Pas de question fermée',
      detail: 'Rien dans la première ligne ne se tranche par un simple oui ou non.',
    });
  }

  // 6. Parasites dans la partie visible : emoji en tête, emoji en rafale, lien, hashtag.
  const noise: string[] = [];
  const leading = visibleText.slice(0, 4);
  if (EMOJI.test(leading)) {
    const idx = leading.search(EMOJI);
    const len = [...visibleText.slice(idx)][0]?.length ?? 1;
    marks.push({ start: idx, end: idx + len, kind: 'bad' });
    noise.push('un emoji ouvre le post');
  }
  const emojiCount = [...visibleText.matchAll(EMOJI_G)].length;
  if (emojiCount > 2) noise.push(`${emojiCount} emojis dans la partie visible`);
  for (const m of visibleText.matchAll(LINK)) {
    const start = m.index ?? 0;
    marks.push({ start, end: start + m[0].length, kind: 'bad' });
    noise.push('un lien dans la partie visible');
    break;
  }
  for (const m of visibleText.matchAll(HASHTAG)) {
    const start = (m.index ?? 0) + m[1].length;
    marks.push({ start, end: start + m[2].length, kind: 'bad' });
    noise.push('un hashtag dans la partie visible');
    break;
  }
  const capsWords = (sentence.match(/\b\p{Lu}{4,}\b/gu) ?? []).length;
  if (capsWords >= 2) noise.push('plusieurs mots en capitales');
  if (noise.length > 0) {
    checks.push({
      id: 'noise',
      severity: 'warning',
      title: `Parasites avant « voir plus » : ${noise.join(', ')}`,
      detail: 'Ces éléments prennent la place du sujet dans les lignes que tout le monde voit, et ils signalent une publication plus qu\'une idée.',
      fix: 'Déplacez emojis, lien et hashtags en fin de texte. Gardez l\'espace visible pour la phrase qui donne envie de lire.',
    });
  } else {
    checks.push({
      id: 'noise',
      severity: 'ok',
      title: 'Partie visible propre',
      detail: 'Ni emoji en tête, ni lien, ni hashtag, ni capitales en rafale avant « voir plus ».',
    });
  }

  const passed = checks.filter((c) => c.severity === 'ok').length;
  return {
    text,
    cutIdx,
    truncated,
    type: classify(firstLine, isOpener),
    firstLine,
    checks,
    marks: resolveMarks(marks),
    passed,
    total: checks.length,
    rewrite,
  };
}

/** Trie et supprime les chevauchements : la première marque posée l'emporte. */
function resolveMarks(marks: HookMark[]): HookMark[] {
  const sorted = [...marks].filter((m) => m.end > m.start).sort((a, b) => a.start - b.start || b.end - a.end);
  const out: HookMark[] = [];
  let lastEnd = -1;
  for (const m of sorted) {
    if (m.start >= lastEnd) {
      out.push(m);
      lastEnd = m.end;
    }
  }
  return out;
}

export interface Segment {
  text: string;
  kind: 'plain' | 'bad' | 'good';
  hidden: boolean;
}

/** Découpe le texte en segments rendables : marques + frontière « voir plus ». */
export function toSegments(result: HookResult): Segment[] {
  const { text, cutIdx, marks, truncated } = result;
  const points = new Set<number>([0, text.length]);
  for (const m of marks) {
    points.add(m.start);
    points.add(Math.min(m.end, text.length));
  }
  if (truncated) points.add(cutIdx);
  const sorted = [...points].filter((p) => p >= 0 && p <= text.length).sort((a, b) => a - b);
  const segments: Segment[] = [];
  for (let i = 0; i < sorted.length - 1; i++) {
    const start = sorted[i];
    const end = sorted[i + 1];
    if (end <= start) continue;
    const mark = marks.find((m) => m.start <= start && m.end >= end);
    segments.push({ text: text.slice(start, end), kind: mark ? mark.kind : 'plain', hidden: truncated && start >= cutIdx });
  }
  return segments;
}

export function reportText(result: HookResult): string {
  const label: Record<Severity, string> = { problem: 'À corriger', warning: 'À surveiller', ok: 'OK' };
  const head = `${HOOK_TYPES[result.type].label} · ${result.passed}/${result.total} contrôles passés`;
  const body = result.checks
    .map((c) => `[${label[c.severity]}] ${c.title}\n${c.detail}${c.fix ? `\n→ ${c.fix}` : ''}`)
    .join('\n\n');
  return `${head}\n\n${body}`;
}
